import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AppointmentDialog.scss';
import cn from 'classnames';
import * as types from 'constants/ActionTypes.js';
import DatePicker from './DatePicker/DatePicker';
import AppointmentForm from './AppointmentForm/AppointmentForm';
import ArrowLeftIcon from 'assets/images/icons/arrow-left.svg';
import ResultSuccessIcon from 'assets/images/icons/result-success.svg';
import ResultErrorIcon from 'assets/images/icons/result-error.svg';
import { useDispatch } from 'react-redux';
import { loadCalendar } from '../../../actions/appointment';
import { OFFLINE_APPOINTMENT_TYPE, ONLINE_APPOINTMENT_TYPE } from '../../../constants';
import { setPopUp } from 'actions/environment';
import { Link } from 'react-router-dom';
import { gaEvent, trackGA } from '../../../services/ga';

const AppointmentDialog = ({ data, buildingId, buildingName, objectType, profile }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState('offline');
  const [isLoading, setIsLoading] = useState(false);
  const [offlineData, setOfflineData] = useState(data);
  const [onlineData, setOnlineData] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [resultStatus, setResultStatus] = useState(null);

  const isStepOne = step === 1;

  const handleTrackEvent = ({ eventLabel }) => {
    trackGA(
      gaEvent.appointmentDialogButtonsClick({
        eventLabel,
        eventContent: buildingName,
        productType: objectType,
        productName: buildingName,
        productID: buildingId,
        user_id: profile.id,
      })
    );
  };

  const handleTabChange = async (newTab) => {
    if (newTab === activeTab) return;

    const eventLabel = newTab === 'online' ? 'onlain' : 'offlain';

    handleTrackEvent({ eventLabel });

    setActiveTab(newTab);
    setIsLoading(true);
    try {
      if (newTab === 'online' && !onlineData) {
        const dataOnline = await dispatch(
          loadCalendar({
            building_id: buildingId,
            type: ONLINE_APPOINTMENT_TYPE,
          })
        );

        if (dataOnline && dataOnline.data) {
          setOnlineData(dataOnline);
        } else {
          dispatch({ type: types.LOAD_CALENDAR_ERROR });
        }
      } else if (newTab === 'offline' && !offlineData) {
        const dataOffline = await dispatch(
          loadCalendar({
            building_id: buildingId,
            type: OFFLINE_APPOINTMENT_TYPE,
          })
        );

        if (dataOffline && dataOffline.data) {
          setOfflineData(dataOffline);
        } else {
          dispatch({ type: types.LOAD_CALENDAR_ERROR });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSlot = (activeDate, slot) => {
    if (slot) {
      setSelectedDate(activeDate);
      setSelectedSlot(slot);
    } else {
      setSelectedDate(null);
      setSelectedSlot(null);
    }
  };

  const handleSetResult = (result) => {
    if (result === 'success') {
      trackGA(
        gaEvent.appointmentDialogConfirmedResultShow({
          eventContent: buildingName,
          productType: objectType,
          productName: buildingName,
          productID: buildingId,
          user_id: profile.id,
        })
      );
    } else {
      trackGA(
        gaEvent.appointmentDialogRejectedResultShow({
          eventContent: buildingName,
          productType: objectType,
          productName: buildingName,
          productID: buildingId,
          user_id: profile.id,
        })
      );
    }

    setResultStatus(result);
  };

  const toPrevStep = () => {
    setSelectedSlot(null);
    setSelectedDate(null);
    setStep(1);
    setResultStatus(null);
  };

  if (resultStatus) {
    if (resultStatus === 'success') {
      return (
        <div className={cn(styles.ResultDialog)}>
          <img src={ResultSuccessIcon} alt="success-icon" className={styles.ResultIcon} />
          <div className={styles.ResultTitle}>
            {t('components.appointment.appointmentDialog.resultSuccessTitle')}
          </div>
          <div className={styles.ResultText}>
            {t('components.appointment.appointmentDialog.resultSuccessText')}
          </div>
          <div className={styles.ButtonsGroup}>
            <button
              className={cn(styles.ResultButton, 'button-primary', 'button-primary--md')}
              onClick={() => {
                handleTrackEvent({ eventLabel: 'horosho' });
                dispatch(setPopUp());
              }}
            >
              {t('components.appointment.appointmentDialog.resultSuccessGood')}
            </button>
            <Link
              to={'/profile?tab=appointments'}
              className={cn(styles.ResultButton, 'button-default', 'button-primary--md')}
              onClick={() => {
                handleTrackEvent({ eventLabel: 'v_moi_zapisi' });
                dispatch(setPopUp());
              }}
            >
              {t('components.appointment.appointmentDialog.resultSuccessToMyAppointment')}
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className={cn(styles.ResultDialog)}>
        <img src={ResultErrorIcon} alt="error-icon" className={styles.ResultIcon} />
        <div className={styles.ResultTitle}>
          {t('components.appointment.appointmentDialog.resultErrorTitle')}
        </div>
        <div className={styles.ResultText}>
          {t('components.appointment.appointmentDialog.resultErrorText')}
        </div>
        <div className={styles.ButtonsGroup}>
          <button
            className={cn(styles.ResultButton, 'button-primary', 'button-primary--md')}
            onClick={() => {
              trackGA(
                gaEvent.appointmentDialogRejectedResultButtonClick({
                  eventLabel: 'perezapisatsya',
                  eventContent: buildingName,
                  productType: objectType,
                  productName: buildingName,
                  productID: buildingId,
                  user_id: profile.id,
                })
              );
              toPrevStep();
            }}
          >
            {t('components.appointment.appointmentDialog.resultErrorNewAppointment')}
          </button>
          <button
            className={cn(styles.ResultButton, 'button-default', 'button-primary--md')}
            onClick={() => {
              trackGA(
                gaEvent.appointmentDialogRejectedResultButtonClick({
                  eventLabel: 'zakryt',
                  eventContent: buildingName,
                  productType: objectType,
                  productName: buildingName,
                  productID: buildingId,
                  user_id: profile.id,
                })
              );
              dispatch(setPopUp());
            }}
          >
            {t('components.appointment.appointmentDialog.resultErrorClose')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.Dialog, { [styles.DialogStepOne]: isStepOne })}>
      <div className={cn(styles.Title, { [styles.TitleStepOne]: isStepOne })}>
        {!isStepOne && (
          <button className={styles.BackButton} onClick={() => setStep(1)}>
            <img src={ArrowLeftIcon} alt="back-icon" />
          </button>
        )}
        {isStepOne
          ? t('components.appointment.appointmentDialog.stepOneTitle')
          : t('components.appointment.appointmentDialog.stepTwoTitle')}
      </div>
      <div className={cn(styles.Content, { [styles.ContentStepOne]: isStepOne })}>
        {isStepOne ? (
          <DatePicker
            data={
              activeTab === 'offline'
                ? offlineData && offlineData.data
                : (onlineData && onlineData.data) || []
            }
            onTabChange={handleTabChange}
            activeTab={activeTab}
            isLoading={isLoading}
            selectedSlot={selectedSlot}
            onSelectSlot={handleSelectSlot}
          />
        ) : (
          <AppointmentForm
            selectedSlot={selectedSlot}
            selectedDate={selectedDate}
            buildingId={buildingId}
            type={activeTab}
            handleSetResult={handleSetResult}
            buildingName={buildingName}
            objectType={objectType}
          />
        )}
      </div>
      <div className={cn(styles.Footer, { [styles.FooterStepOne]: isStepOne })}>
        {isStepOne && (
          <button
            className={cn(styles.NextButton, 'button-primary', 'button-primary--md')}
            onClick={() => {
              handleTrackEvent({ eventLabel: 'dalee' });
              setStep(2);
            }}
            disabled={!selectedSlot}
          >
            {t('components.appointment.appointmentDialog.nextButton')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AppointmentDialog;



// WEBPACK FOOTER //
// ./src/components/Appointment/AppointmentDialog/AppointmentDialog.jsx